import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getFormsApi,
  addFormApi,
  cloneFormApi,
  editFormApi,
  deleteFormApi,
  togglePublishApi,
} from "../services/dynamicForms";
import { toast } from "react-hot-toast";
import CustomToaster from "../components/Toaster";
import { TOAST_TIME, TOAST_TITLE, TOAST_TYPE } from "../constants/constants";



export const fetchForms = createAsyncThunk(
  "forms/fetch",
  async (data, { rejectWithValue }) => {
    try {
      const { offset } = data || {};
      const response = await getFormsApi(offset);
      return response.data;
    } catch (error) {

      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createForm = createAsyncThunk(
  "forms/create",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await addFormApi(data);
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.SUCCESS}
          title={TOAST_TITLE.SUCCESS}
          time={TOAST_TIME.FIVE}
          message={"Form created successfully."}
        />
      );
      dispatch(fetchForms({ offset: 0 }));
      return response.data;
    } catch (error) {
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.ERROR}
          time={TOAST_TIME.FIVE}
          message={"Failed to create form."}
        />
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cloneForm = createAsyncThunk(
  "forms/clone",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await cloneFormApi(data);
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.SUCCESS}
          title={TOAST_TITLE.SUCCESS}
          time={TOAST_TIME.FIVE}
          message={"Form cloned successfully."}
        />
      );
      dispatch(fetchForms({ offset: 0 }));
      return response.data;
    } catch (error) {
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.ERROR}
          time={TOAST_TIME.FIVE}
          message={"Failed to clone form."}
        />
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateForm = createAsyncThunk(
  "forms/update",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await editFormApi(data);
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.SUCCESS}
          title={TOAST_TITLE.SUCCESS}
          time={TOAST_TIME.FIVE}
          message={"Form updated successfully."}
        />
      );
      dispatch(fetchForms({ offset: data.offset || 0 }));
      return response.data;
    } catch (error) {
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.ERROR}
          time={TOAST_TIME.FIVE}
          message={"Failed to update form."}
        />
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeForm = createAsyncThunk(
  "forms/delete",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await deleteFormApi(id);
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.SUCCESS}
          title={TOAST_TITLE.SUCCESS}
          time={TOAST_TIME.FIVE}
          message={"Form deleted successfully."}
        />
      );
      dispatch(fetchForms({ offset: 0 }));
      return id;
    } catch (error) {
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.ERROR}
          time={TOAST_TIME.FIVE}
          message={"Failed to delete form."}
        />
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const publishForm = createAsyncThunk(
  "forms/publish",
  async ({ id, published, data , offset}, { dispatch, rejectWithValue }) => {
    try {
      const response = await togglePublishApi(id, published, data);
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.SUCCESS}
          title={TOAST_TITLE.SUCCESS}
          time={TOAST_TIME.FIVE}
          message={
            published
              ? "Form published successfully."
              : "Form unpublished successfully."
          }
        />
      );
      dispatch(fetchForms({ offset: offset || 0 }));
      return response.data;
    } catch (error) {
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.ERROR}
          time={TOAST_TIME.FIVE}
          message={"Failed to update publish status."}
        />
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);



const formSlice = createSlice({
  name: "forms",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(fetchForms.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.error = null;
      })
      .addCase(fetchForms.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload || [];
      })
      .addCase(fetchForms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createForm.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.error = null;
      })
      .addCase(createForm.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(cloneForm.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.error = null;
      })
      .addCase(cloneForm.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(cloneForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateForm.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(removeForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeForm.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(publishForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(publishForm.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(publishForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
  },
});

export default formSlice.reducer;
